import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuthState } from "@/data/user";
import {
  useCurrentChatHistory,
  useRenameCurrentSessionMutation,
  useArchiveCurrentSessionMutation,
  useUnarchiveCurrentSessionMutation,
  useShareCurrentSessionMutation,
  useUnshareCurrentSessionMutation,
  usePinCurrentSessionMutation,
  useUnpinCurrentSessionMutation,
} from "@/data/chat-history";
import {
  listChatHistory,
  chatHistoryAction,
  type ChatHistorySession,
} from "@/lib/apis/web/chat-history-api";

const POLL_INTERVAL_MS = 120_000;

interface UseChatHistoryOptions {
  projectId?: string | null;
  enabled?: boolean;
  requestHeaders?: HeadersInit;
}

interface UseChatHistoryReturn {
  personal: ChatHistorySession[];
  project: ChatHistorySession[];
  loading: boolean;
  error: string | null;
  isReactive: boolean;
  refetch: () => void;
  actions: {
    rename: (sessionId: string, customTitle: string) => Promise<void>;
    archive: (sessionId: string) => Promise<void>;
    unarchive: (sessionId: string) => Promise<void>;
    share: (sessionId: string) => Promise<void>;
    unshare: (sessionId: string) => Promise<void>;
    pin: (sessionId: string) => Promise<void>;
    unpin: (sessionId: string) => Promise<void>;
    /** Archives the given session ids in parallel, then refetches once. */
    archiveManySessionIds: (sessionIds: string[]) => Promise<void>;
    /** Archives every active thread in the current list (personal + project) in one refetch. */
    archiveAllActive: () => Promise<void>;
  };
}

type ChatHistoryActionName =
  | "rename"
  | "archive"
  | "unarchive"
  | "share"
  | "unshare"
  | "pin"
  | "unpin";

export function useChatHistory({
  projectId,
  enabled = true,
  requestHeaders,
}: UseChatHistoryOptions): UseChatHistoryReturn {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuthState();
  const [personalFallback, setPersonalFallback] = useState<
    ChatHistorySession[]
  >([]);
  const [projectFallback, setProjectFallback] = useState<
    ChatHistorySession[]
  >([]);
  const [fallbackLoading, setFallbackLoading] = useState(false);
  const [fallbackError, setFallbackError] = useState<string | null>(null);
  const fetchCountRef = useRef(0);
  const isReactive = enabled && isAuthenticated;
  const shouldUseFallback = enabled && !isAuthenticated && !isAuthLoading;
  const reactiveQueryArgs = useMemo(
    () =>
      isReactive
        ? ({
            projectId: projectId ?? undefined,
            status: "active",
          } as const)
        : "skip",
    [isReactive, projectId],
  );

  const reactiveResult = useCurrentChatHistory(reactiveQueryArgs) as
    | {
        personal: ChatHistorySession[];
        project: ChatHistorySession[];
      }
    | undefined;

  const renameCurrentSession = useRenameCurrentSessionMutation();
  const archiveCurrentSession = useArchiveCurrentSessionMutation();
  const unarchiveCurrentSession = useUnarchiveCurrentSessionMutation();
  const shareCurrentSession = useShareCurrentSessionMutation();
  const unshareCurrentSession = useUnshareCurrentSessionMutation();
  const pinCurrentSession = usePinCurrentSessionMutation();
  const unpinCurrentSession = useUnpinCurrentSessionMutation();

  const fetchHistory = useCallback(async () => {
    if (!shouldUseFallback) {
      return;
    }

    const fetchId = ++fetchCountRef.current;
    setFallbackLoading(true);
    setFallbackError(null);

    try {
      const result = await listChatHistory(
        {
          projectId: projectId ?? undefined,
          status: "active",
        },
        {
          headers: requestHeaders,
        },
      );
      if (fetchId !== fetchCountRef.current) return;
      startTransition(() => {
        setPersonalFallback(result.personal);
        setProjectFallback(result.project);
      });
    } catch (err) {
      if (fetchId !== fetchCountRef.current) return;
      setFallbackError(err instanceof Error ? err.message : String(err));
    } finally {
      if (fetchId === fetchCountRef.current) {
        setFallbackLoading(false);
      }
    }
  }, [requestHeaders, shouldUseFallback, projectId]);

  useEffect(() => {
    if (!shouldUseFallback) {
      startTransition(() => {
        setPersonalFallback([]);
        setProjectFallback([]);
        setFallbackError(null);
        setFallbackLoading(false);
      });
      return;
    }

    void fetchHistory();
    const interval = window.setInterval(() => {
      void fetchHistory();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [fetchHistory, shouldUseFallback]);

  const performReactiveAction = useCallback(
    async (
      action: ChatHistoryActionName,
      sessionId: string,
      params?: Record<string, unknown>,
    ) => {
      const payload = { sessionId: sessionId as any };
      switch (action) {
        case "rename":
          await renameCurrentSession({
            ...payload,
            customTitle: String(params?.customTitle ?? ""),
          });
          return;
        case "archive":
          await archiveCurrentSession(payload);
          return;
        case "unarchive":
          await unarchiveCurrentSession(payload);
          return;
        case "share":
          if (!projectId) {
            throw new Error("Cannot share a session without a project.");
          }
          await shareCurrentSession({
            ...payload,
            projectId: projectId as any,
          });
          return;
        case "unshare":
          await unshareCurrentSession(payload);
          return;
        case "pin":
          await pinCurrentSession(payload);
          return;
        case "unpin":
          await unpinCurrentSession(payload);
          return;
      }
    },
    [
      archiveCurrentSession,
      pinCurrentSession,
      projectId,
      renameCurrentSession,
      shareCurrentSession,
      unarchiveCurrentSession,
      unpinCurrentSession,
      unshareCurrentSession,
    ],
  );

  const performAction = useCallback(
    async (
      action: ChatHistoryActionName,
      sessionId: string,
      params?: Record<string, unknown>,
    ) => {
      if (isReactive) {
        await performReactiveAction(action, sessionId, params);
        return;
      }
      let scopedParams = params;
      if (action === "share") {
        if (!projectId) {
          throw new Error("Cannot share a session without a project.");
        }
        scopedParams = { ...params, projectId };
      }
      await chatHistoryAction(action, sessionId, scopedParams, {
        headers: requestHeaders,
      });
      await fetchHistory();
    },
    [fetchHistory, isReactive, performReactiveAction, projectId, requestHeaders],
  );

  const archiveManySessionIds = useCallback(
    async (sessionIds: string[]) => {
      const ids = [...new Set(sessionIds)];
      if (ids.length === 0) return;
      if (isReactive) {
        await Promise.all(
          ids.map((sessionId) =>
            archiveCurrentSession({ sessionId: sessionId as any }),
          ),
        );
        return;
      }
      await Promise.all(
        ids.map((sessionId) =>
          chatHistoryAction("archive", sessionId, undefined, {
            headers: requestHeaders,
          }),
        ),
      );
      await fetchHistory();
    },
    [archiveCurrentSession, fetchHistory, isReactive, requestHeaders],
  );

  const personal = reactiveResult?.personal ?? personalFallback;
  const project = reactiveResult?.project ?? projectFallback;

  const archiveAllActive = useCallback(async () => {
    await archiveManySessionIds([
      ...personal.map((s) => s._id),
      ...project.map((s) => s._id),
    ]);
  }, [personal, project, archiveManySessionIds]);

  const actions = useMemo(
    () => ({
      rename: (sessionId: string, customTitle: string) =>
        performAction("rename", sessionId, { customTitle }),
      archive: (sessionId: string) => performAction("archive", sessionId),
      unarchive: (sessionId: string) => performAction("unarchive", sessionId),
      share: (sessionId: string) => performAction("share", sessionId),
      unshare: (sessionId: string) => performAction("unshare", sessionId),
      pin: (sessionId: string) => performAction("pin", sessionId),
      unpin: (sessionId: string) => performAction("unpin", sessionId),
      archiveManySessionIds,
      archiveAllActive,
    }),
    [archiveAllActive, archiveManySessionIds, performAction],
  );

  const loading = isReactive
    ? reactiveResult === undefined
    : enabled && isAuthLoading
      ? true
      : fallbackLoading;
  const refetch = useCallback(() => {
    if (!isReactive) {
      void fetchHistory();
    }
  }, [fetchHistory, isReactive]);

  return {
    personal,
    project,
    loading,
    error: isReactive ? null : fallbackError,
    isReactive,
    refetch,
    actions,
  };
}
